import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import {
  Sparkles, Camera, Stethoscope, MessageCircle, ChevronRight, QrCode, ArrowRight,
} from "lucide-react";
import { Shell, Disclaimer } from "../components/Shell";
import { CLINIC, DISCLAIMER, COMMON_QUESTIONS } from "../lib/data";
import { API, api } from "../lib/api";

const FEATURES = [
  { icon: Sparkles, label: "AI screening" },
  { icon: Camera, label: "Guided photos" },
  { icon: Stethoscope, label: "Dentist review" },
];

const STEPS = [
  "Tell us about you",
  "Describe the problem",
  "Take six guided photos",
  "Get an instant screening",
  "Ask DentaFriend anything",
];

export default function Dashboard() {
  const navigate = useNavigate();
  const qrSrc = `${API}/qrcode?url=${encodeURIComponent(typeof window !== "undefined" ? window.location.origin : "")}`;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const src = new URLSearchParams(window.location.search).get("src");
    if (!src) return;
    if (sessionStorage.getItem("muse_scan_tracked") === src) return;
    sessionStorage.setItem("muse_scan_tracked", src);
    api.post("/scan-event", { source: src }).catch(() => {});
  }, []);

  return (
    <Shell>
      <div className="flex-1 flex flex-col pb-3">
        {/* Hero */}
        <div className="brand-gradient px-6 pt-8 pb-9 rounded-b-[28px] relative overflow-hidden">
          {/* 3D dental background */}
          <div
            data-testid="hero-3d-bg"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-40 mix-blend-screen"
            style={{ backgroundImage: "url('/assets/dental-3d-pattern.jpg')" }}
          />
          <img
            src="/assets/dental-3d-hero.jpg"
            aria-hidden="true"
            alt=""
            className="pointer-events-none absolute -right-6 -top-2 w-36 h-auto opacity-70 mix-blend-screen select-none"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#123a2b]/95 via-[#123a2b]/70 to-transparent" />

          <div className="relative z-10">
            <span
              data-testid="ai-badge"
              className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold tracking-wide text-[#8FE3BE] bg-white/10 px-2.5 py-1 rounded-full"
            >
              <Sparkles className="w-3 h-3" /> AI-ASSISTED SCREENING
            </span>
            <h1 className="mt-3 font-head font-extrabold tracking-tight text-[26px] leading-[1.1] text-white">
              How healthy are your teeth?
            </h1>
            <p className="mt-2 text-[13px] leading-snug text-white/80">
              {CLINIC.tagline}. Instant educational screening — no download, no login.
            </p>
          </div>
        </div>

        {/* Feature strip overlapping hero */}
        <div className="px-6 -mt-6 relative z-10">
          <div className="bg-card card-elev rounded-2xl grid grid-cols-3 divide-x divide-border">
            {FEATURES.map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-1.5 py-2.5">
                <div className="w-8 h-8 rounded-xl bg-accent flex items-center justify-center">
                  <f.icon className="w-4 h-4 text-primary" />
                </div>
                <span className="text-[11px] font-medium text-foreground">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 mt-4 flex flex-col gap-3">
          {/* Ask DentaFriend */}
          <button
            data-testid="ask-dentafriend-card"
            onClick={() => navigate("/chat")}
            className="tap flex items-center gap-3 rounded-2xl brand-gradient-soft p-3 text-left mint-glow"
          >
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#8FE3BE]" />
            </div>
            <div className="flex-1">
              <p className="font-head font-semibold text-[14px] text-white">Ask DentaFriend</p>
              <p className="text-[12px] text-white/75">Free dental Q&A — no photos needed.</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/70" />
          </button>

          {/* What's bothering you */}
          <div>
            <h2 className="font-head font-bold text-base tracking-tight mb-2">What's bothering you?</h2>
            <div className="grid grid-cols-2 gap-2">
              {COMMON_QUESTIONS.map((c) => (
                <button
                  key={c.label}
                  data-testid={`common-q-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => navigate(`/chat?q=${encodeURIComponent(c.q)}`)}
                  className="tap rounded-xl border border-border bg-card px-3 py-2 text-left text-[13px] font-medium text-foreground hover:border-primary/40"
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* How it works */}
          <div className="rounded-2xl bg-secondary/60 border border-border p-3.5">
            <h2 className="font-head font-bold text-base tracking-tight mb-2">How it works</h2>
            <ol className="grid grid-cols-1 gap-1.5">
              {STEPS.map((s, i) => (
                <li key={s} className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-[13px] text-foreground">{s}</span>
                </li>
              ))}
            </ol>
          </div>

          <button
            data-testid="dashboard-qr-card"
            onClick={() => navigate("/qr")}
            className="tap flex items-center gap-3 rounded-2xl brand-gradient-soft p-3 text-left mint-glow"
          >
            <div className="w-14 h-14 rounded-xl bg-white p-1 shrink-0">
              <img data-testid="dashboard-qr-image" src={qrSrc} alt="Scan to open Muse Dental AI" className="w-full h-full" />
            </div>
            <div className="flex-1">
              <p className="font-head font-semibold text-[14px] text-white">Scan. Screen. Smile.</p>
              <p className="text-[12px] text-white/75">Scan or share — opens on any phone.</p>
            </div>
            <QrCode className="w-5 h-5 text-white/70" />
          </button>

          <Disclaimer testId="dashboard-disclaimer">{DISCLAIMER}</Disclaimer>
        </div>
      </div>

      {/* Sticky CTA */}
      <div className="sticky bottom-0 max-w-md w-full px-6 py-3 bg-background border-t border-border">
        <button
          data-testid="start-screening-btn"
          onClick={() => navigate("/intake")}
          className="tap w-full h-13 py-3.5 rounded-2xl brand-gradient text-white font-semibold text-base flex items-center justify-center gap-2"
        >
          Start Screening <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </Shell>
  );
}
